import { first, last, startsWith, pickBy } from 'lodash';
import { flow } from 'lodash/fp';
/**
 * Multi series channels name helpers
 */

/**
 * Allows to get name for multi series channel without index
 * @param {string} channelName - name of the channel
 * @returns {string} - multi series channel name
 */
const getMultiSeriesNamePrefix = channelName => `${channelName}_series`;

/**
 * Builds name for multi series channel
 * @param {string} channelName - name of channel
 * @param {number} index - index of multi series channel
 * @returns {string} - multi series channel name
 */
const buildMultiSeriesName = (channelName, index) =>
  `${channelName}_series_${index}`;

/**
 * Checks if channel name is multi series channel name
 * @param {string} name - channel name
 * @returns {boolean} - is channel multi series channel
 */
const isMultiSeriesName = name => /[A-Za-z]+_series_\d+/.test(name);

/**
 * Retrieves parent channel name if multi series channel name is passed
 * @param {String} channelName - channel name. It could be regular channel or multi series
 * @returns {String} - regular channel
 */
const getMultiSeriesParentChannel = channelName =>
  isMultiSeriesName(channelName) ? first(channelName.split('_')) : channelName;

/**
 * Retrieves the number of a multi series channel if it exists, otherwise returns -1.
 * @param {String} channelName - channel name. Works with either regular channels for multi series
 * @returns {Number} - Number of the multiseries if it exists, otherwise -1
 */
const getMultiSeriesValue = channelName =>
  isMultiSeriesName(channelName)
    ? parseInt(last(channelName.split('_')), 10)
    : -1;

/**
 * Replaces parent channels name in multi series name with new one:
 *   y_series_0 -> x_series_0
 * @param {String} channelName - multi series channel name
 * @param {String} newRegularChannelName - new regular channel name
 * @returns {String} - multi series channel name with replaced parent
 */
const replaceMultiSeriesParentChannelName = (
  channelName,
  newRegularChannelName
) =>
  flow(
    name => name.split('_'),
    values => [newRegularChannelName, ...values.slice(1)],
    values => values.join('_')
  )(channelName);

/**
 * Returns object with only multi series fields
 * @param {Object} channels - channels object from the store
 * @param {String} regularChannelName - regular channel name (parent)
 * @returns {Object} - multi series channels object
 */
const getMultiSeriesChannels = (channels, regularChannelName) =>
  pickBy(channels, (encoding, seriesChannelName) =>
    startsWith(seriesChannelName, getMultiSeriesNamePrefix(regularChannelName))
  );

export {
  getMultiSeriesNamePrefix,
  buildMultiSeriesName,
  isMultiSeriesName,
  getMultiSeriesParentChannel,
  getMultiSeriesValue,
  replaceMultiSeriesParentChannelName,
  getMultiSeriesChannels,
};
